import React from "react";
import { Button } from "primereact/button";
import { Slider, SliderChangeEvent } from "primereact/slider";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import ProjectionsIcon from "app/assets/IconComponent/ProjectionsIcon";
import CalendarIcon from "app/assets/IconComponent/CalendarIcon";
import CandleCake from "app/assets/IconComponent/CandleCake";
import InsuredIcon from "app/assets/IconComponent/InsuredIcon";

interface AssetValueChartFilterProps {
  selectedChartFilter: string;
  onChartFilterChange: (filter: string) => void;
  showProjections: boolean;
  onProjectionsToggle: () => void;
  enableAgeFilter?: boolean;
  enablePlanYearFilter?: boolean;
  enableZoom?: boolean;
  zoomValue?: [number, number];
  zoomMax?: number;
  onZoomChange?: (value: [number, number]) => void;
  hideProjection?: boolean;
  overrideCalendarIcon?: boolean;
}

const AssetValueChartFilter: React.FC<AssetValueChartFilterProps> = ({
  selectedChartFilter,
  onChartFilterChange,
  showProjections,
  onProjectionsToggle,
  enableAgeFilter = true,
  enablePlanYearFilter = true,
  enableZoom = false,
  zoomValue,
  zoomMax,
  onZoomChange,
  overrideCalendarIcon = false,
  hideProjection = false,
}) => {
  const globalTheme = getSessionStore("globalTheme");
  const isDark = globalTheme === THEME.DARK;
  // Every option is shown side by side as a segmented control (Figma). It used to be a single
  // button that cycled to the next filter on each click, so reaching a filter meant clicking
  // through the others and the current selection was the only one visible.
  const chartFilterOptions = [
    {
      // IAPP calendar for calendar-year everywhere (the react-icons month grid was the
      // odd one out; overrideCalendarIcon kept for callers but both paths now match).
      renderIcon: (fill: string) => (
        <CalendarIcon width={overrideCalendarIcon ? 30 : 20} fill={fill} />
      ),
      value: "year",
      tooltip: "Year",
    },
    {
      renderIcon: (fill: string) => <CandleCake width={20} height={18} fill={fill} />,
      value: "planYear",
      tooltip: "Plan Year",
    },
    ...(enableAgeFilter
      ? [
          {
            renderIcon: (fill: string) => <InsuredIcon width="22" height="17" fill={fill} />,
            value: "insuredAge",
            tooltip: "Insured Age",
          },
        ]
      : []),
  ].filter((option) => enablePlanYearFilter || option.value !== "planYear");

  // Selected/unselected icon colors follow the projections toggle below, so both controls in
  // this row read the same way.
  const selectedFilterColor = isDark ? "#4A90E2" : "#004781";
  const unselectedFilterColor = isDark ? "#6B7280" : "#9CA3AF";

  const projectionsButtonColor = (() => {
    if (showProjections) {
      return isDark ? "#4A90E2" : "#004781";
    }
    return isDark ? "#6B7280" : "#9CA3AF";
  })();

  const shouldShowZoom = enableZoom && typeof zoomMax === "number" && zoomMax > 0 && !!zoomValue;

  const handleZoomChange = (e: SliderChangeEvent) => {
    if (!onZoomChange) return;
    const { value } = e;
    if (!Array.isArray(value) || value.length !== 2) return;
    const [start, end] = value;
    if (typeof start !== "number" || typeof end !== "number") return;
    onZoomChange([start, end]);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row space-x-2 align-items-center justify-content-between">
        {/* Left side - Chart filter segmented control */}
        <div
          className="flex flex-row align-items-center gap-1 p-1"
          style={{
            background: isDark ? "#1F2937" : "#F9FAFB",
            border: `1px solid ${isDark ? "#3A3A50" : "#E5E7EB"}`,
            borderRadius: 8,
          }}
        >
          {chartFilterOptions.map((option) => {
            const isSelected = option.value === selectedChartFilter;
            return (
              <Button
                key={option.value}
                icon={option.renderIcon(isSelected ? selectedFilterColor : unselectedFilterColor)}
                text
                size="small"
                className="p-2"
                tooltipOptions={{ position: "top" }}
                tooltip={option.tooltip}
                aria-pressed={isSelected}
                onClick={() => onChartFilterChange(option.value)}
                style={{
                  // Only the selected pill sets a background; leaving the key off entirely for
                  // the others lets PrimeReact's hover styling through (an inline
                  // "transparent" would outrank it).
                  ...(isSelected ? { background: isDark ? "#374151" : "#E5E7EB" } : {}),
                  borderRadius: 6,
                }}
              />
            );
          })}
        </div>

        {/* Right side - Projections toggle */}
        {!hideProjection && (
          <div className="flex flex-row align-items-center gap-2">
            <Button
              icon={
                <ProjectionsIcon
                  key={`projections-icon-${showProjections}`}
                  fill={
                    showProjections
                      ? isDark
                        ? "#4A90E2"
                        : "#004781"
                      : isDark
                      ? "#6B7280"
                      : "#9CA3AF"
                  }
                />
              }
              text
              size="small"
              className="p-2 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:shadow-none"
              severity="secondary"
              tooltipOptions={{ position: "top" }}
              tooltip={showProjections ? "Hide Projections" : "Show Projections"}
              onClick={onProjectionsToggle}
              style={{
                color: projectionsButtonColor,
                outline: "none",
                boxShadow: "none",
              }}
              pt={{
                root: {
                  className: "!shadow-none focus:!shadow-none focus-visible:!shadow-none",
                },
              }}
            />
          </div>
        )}
      </div>

      {shouldShowZoom ? (
        <div className="px-1">
          <Slider
            value={zoomValue}
            min={0}
            max={zoomMax}
            range
            onChange={handleZoomChange}
            pt={{
              range: { className: isDark ? "!bg-[#A7B7CC4D]" : "!bg-[#A7B7CC66]" },
              handle: {
                className: isDark ? "!border-none !bg-white" : "!border-none !bg-[#333333]",
              },
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(AssetValueChartFilter);
